import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { getNotificationModuleIcon } from 'app/layout/common/notifications/notification-module.icons';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector       : 'notifications',
    templateUrl    : './notifications.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'notifications',
    standalone     : true,
    imports        : [MatButtonModule, NgIf, MatIconModule, MatTooltipModule, NgFor, NgClass, DatePipe, TranslocoModule],
    styles         : [`
        .sai-notifications-panel {
            background: var(--sai-surface);
            color: var(--sai-text);
        }
        .sai-notifications-panel__surface {
            background: var(--sai-surface);
        }
        .sai-notifications-panel__divider {
            border-color: var(--sai-border);
        }
        .notification-panel-text {
            min-width: 0;
            max-width: 100%;
            overflow-wrap: anywhere;
            word-break: break-word;
        }
        .sai-notifications-panel__icon-badge {
            background: var(--sai-surface-alt);
        }
        .sai-notifications-panel__icon-badge-glyph {
            color: var(--sai-text-secondary) !important;
        }
        .sai-notifications-panel__title {
            color: var(--sai-text);
        }
        .sai-notifications-panel__muted {
            color: var(--sai-text-secondary);
        }
        .sai-notifications-panel__tablist {
            background: var(--sai-surface-alt);
        }
        .sai-notifications-panel__tab {
            color: var(--sai-text-secondary);
        }
        .sai-notifications-panel__tab--active {
            background: var(--sai-surface);
            color: var(--sai-text);
            box-shadow: var(--sai-shadow-xs);
        }
        .sai-notifications-panel__tab-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 1.25rem;
            height: 1.25rem;
            padding: 0 0.25rem;
            border-radius: 0.25rem;
            background: color-mix(in srgb, var(--sai-border) 65%, transparent);
            color: var(--sai-text-secondary);
            font-size: 0.6875rem;
            font-weight: 600;
            line-height: 1;
        }
        .sai-notifications-panel__tab--active .sai-notifications-panel__tab-badge {
            background: var(--sai-surface-alt);
        }
        .sai-notifications-panel__card--read {
            background: var(--sai-surface);
            border-color: var(--sai-border);
        }
        .sai-notifications-panel__card--unread {
            background: color-mix(in srgb, var(--c1, #3dc3fb) 10%, transparent);
            border-color: color-mix(in srgb, var(--c1, #3dc3fb) 40%, transparent);
            box-shadow: inset 3px 0 0 color-mix(in srgb, var(--c1, #3dc3fb) 75%, transparent);
        }
        .sai-notifications-panel__icon-btn {
            width: 1.75rem !important;
            height: 1.75rem !important;
            min-height: 1.75rem !important;
            padding: 0 !important;
        }
        /* The touch target must not exceed the visible box, or the Remove target overlaps
           the adjacent expand chevron and keeps its hover/tooltip (and steals clicks). */
        .sai-notifications-panel__icon-btn .mat-mdc-button-touch-target {
            width: 100% !important;
            height: 100% !important;
        }
        .sai-notifications-panel__icon-btn .mat-icon {
            color: var(--sai-text-secondary);
        }
        .sai-notifications-panel__okay-btn,
        .sai-notifications-panel__center-btn {
            border-color: var(--sai-border) !important;
            color: var(--sai-text) !important;
        }
    `],
})
export class NotificationsComponent implements OnInit, OnDestroy
{
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[];
    unreadCount: number = 0;
    activeFilter: 'all' | 'unread' | 'read' = 'all';
    expandedIds = new Set<string>();
    lang: string = 'en';
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _translocoService: TranslocoService,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this.lang = this._translocoService.getActiveLang();
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((lang: string) =>
            {
                this.lang = lang;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to notification changes
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) =>
            {
                // Load the notifications
                this.notifications = notifications;

                // Calculate the unread count
                this._calculateUnreadCount();

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if ( this._overlayRef )
        {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void
    {
        // Return if the notifications panel or its origin is not defined
        if ( !this._notificationsPanel || !this._notificationsOrigin )
        {
            return;
        }

        // Create the overlay if it doesn't exist
        if ( !this._overlayRef )
        {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void
    {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void
    {
        this._notificationsService.markAllAsRead().subscribe();
    }

    setActiveFilter(filter: 'all' | 'unread' | 'read'): void
    {
        this.activeFilter = filter;
        this._changeDetectorRef.markForCheck();
    }

    deleteAllNotifications(): void
    {
        const list = this.notifications ?? [];
        if (!list.length)
        {
            return;
        }

        list.forEach((notification) =>
        {
            this._notificationsService.delete(notification.id).subscribe();
        });
        this.closePanel();
    }

    toggleExpand(notification: Notification): void
    {
        const id = String(notification.id);
        if (this.expandedIds.has(id))
        {
            this.expandedIds.delete(id);
        }
        else
        {
            this.expandedIds.add(id);
        }
        this._changeDetectorRef.markForCheck();
    }

    isExpanded(notification: Notification): boolean
    {
        return this.expandedIds.has(String(notification.id));
    }

    get allCount(): number
    {
        return (this.notifications ?? []).length;
    }

    get unreadTabCount(): number
    {
        return this.unreadCount;
    }

    get readTabCount(): number
    {
        return (this.notifications ?? []).filter(n => n.read).length;
    }

    get filteredNotifications(): Notification[]
    {
        const list = this.notifications ?? [];
        if (this.activeFilter === 'unread')
        {
            return list.filter(n => !n.read);
        }
        if (this.activeFilter === 'read')
        {
            return list.filter(n => n.read);
        }
        return list;
    }

    getNotificationTitle(notification: Notification): string
    {
        return notification.title ?? notification.description ?? 'Notification';
    }

    getNotificationIconStyle(notification: Notification): { icon: string; bg: string; text: string }
    {
        const haystack = `${notification.title ?? ''} ${notification.description ?? ''}`.toLowerCase();
        let tag = 'report';
        if (haystack.includes('data') || haystack.includes('upload'))
        {
            tag = 'data_source';
        }
        else if (haystack.includes('task') || haystack.includes('question') || haystack.includes('autonomous activity'))
        {
            tag = 'question';
        }
        else if (haystack.includes('chat'))
        {
            tag = 'ai_chat_share';
        }
        else if (haystack.includes('procedure'))
        {
            tag = 'procedure';
        }
        return getNotificationModuleIcon(tag);
    }

    acknowledgeNotification(notification: Notification, event?: MouseEvent): void
    {
        event?.stopPropagation();
        if (!notification.read)
        {
            notification.read = true;
            this._notificationsService.update(notification.id, notification).subscribe();
            this._calculateUnreadCount();
        }
        this.expandedIds.delete(String(notification.id));
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: Notification): void
    {
        // Toggle the read status
        notification.read = !notification.read;

        // Update the notification
        this._notificationsService.update(notification.id, notification).subscribe();
    }

    /**
     * Delete the given notification
     */
    delete(notification: Notification): void
    {
        // Delete the notification
        this._notificationsService.delete(notification.id).subscribe();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void
    {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop     : true,
            backdropClass   : 'fuse-backdrop-on-mobile',
            scrollStrategy  : this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX : 'start',
                        originY : 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX : 'start',
                        originY : 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX : 'end',
                        originY : 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX : 'end',
                        originY : 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() =>
        {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void
    {
        let count = 0;

        if ( this.notifications && this.notifications.length )
        {
            count = this.notifications.filter(notification => !notification.read).length;
        }

        this.unreadCount = count;
    }
}
